import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";

import { getApi } from "../../../api/api";
import { SaleItemInterface } from "../../../common/types/interfaces";

export interface UseItemDetailsResult {
  item: SaleItemInterface | null;
  ready: boolean;
  displayPrice: number;
  hasParentPrice: boolean;
  children: any[];
}

export const useItemDetails = (): UseItemDetailsResult => {
  const { id } = useParams<{ id: string }>();
  const [item, setItem] = useState<SaleItemInterface | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);

    getApi()
      .getItem(Number(id))
      .then((data) => {
        if (!cancelled) {
          setItem(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItem(null);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const { displayPrice, hasParentPrice, children } = useMemo(() => {
    if (!item) {
      return { displayPrice: 0, hasParentPrice: false, children: [] as any[] };
    }

    const price = item.prices?.[0]?.price || 0;
    const safeItems = Array.isArray(item.items) ? item.items : [];
    const hasParentPrice = price > 0;

    if (!hasParentPrice && safeItems.length > 0) {
      const sum = safeItems.reduce((acc: number, kit: any) => {
        const childPrice = kit.item?.prices?.[0]?.price || 0;
        return acc + childPrice * (kit.quantity || 1);
      }, 0);
      return { displayPrice: sum, hasParentPrice: false, children: safeItems };
    }

    return { displayPrice: price, hasParentPrice, children: safeItems };
  }, [item]);

  return { item, ready: !loading, displayPrice, hasParentPrice, children };
};